import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";

const ModeContext = createContext(null);
// Non-sensitive UI preference stored in localStorage (safe for XSS — no auth/PII data)
const MODE_KEY = "app-mode";
const DENSITY_KEY = "app-density";
const VALID_MODES = new Set(["work", "game", "minimalist", "adhd"]);
const VALID_DENSITIES = new Set(["normal", "compact"]);

export const MODE_CONFIG = {
  work: {
    label: "Work",
    defaultTheme: "mmntm-dark",
    showGameFeatures: false,
    showADHDFeatures: false,
    animationIntensity: "subtle",
    description: "Professional productivity dashboard",
    styleType: "professional",
  },
  game: {
    label: "Game",
    defaultTheme: "retro-cyber",
    showGameFeatures: true,
    showADHDFeatures: false,
    animationIntensity: "strong",
    description: "Immersive gamified experience",
    styleType: "game",
  },
  minimalist: {
    label: "Minimal",
    defaultTheme: "minimalist-bw",
    showGameFeatures: false,
    showADHDFeatures: false,
    animationIntensity: "none",
    description: "Pure black & white focus",
    styleType: "minimal",
  },
  adhd: {
    label: "ADHD",
    defaultTheme: "adhd-calm",
    showGameFeatures: false,
    showADHDFeatures: true,
    animationIntensity: "gentle",
    description: "Cognitive support & focus funnel",
    styleType: "adhd",
  },
};

export function ModeProvider({ children, onModeChange }) {
  const [mode, setModeRaw] = useState(() => {
    const stored = localStorage.getItem(MODE_KEY);
    return VALID_MODES.has(stored) ? stored : "work";
  });
  const [density, setDensityRaw] = useState(() => {
    const stored = localStorage.getItem(DENSITY_KEY);
    return VALID_DENSITIES.has(stored) ? stored : "normal";
  });

  const setMode = useCallback((newMode) => {
    if (!VALID_MODES.has(newMode)) return;
    setModeRaw(newMode);
    localStorage.setItem(MODE_KEY, newMode);
    if (onModeChange) onModeChange(newMode);
  }, [onModeChange]);

  const setDensity = useCallback((newDensity) => {
    if (!VALID_DENSITIES.has(newDensity)) return;
    setDensityRaw(newDensity);
    localStorage.setItem(DENSITY_KEY, newDensity);
  }, []);

  const toggleCompact = useCallback(() => {
    setDensity(density === "compact" ? "normal" : "compact");
  }, [density, setDensity]);

  // Set data-mode and data-density attributes on root for CSS targeting
  useEffect(() => {
    document.documentElement.setAttribute("data-mode", mode);
  }, [mode]);

  useEffect(() => {
    document.documentElement.setAttribute("data-density", density);
  }, [density]);

  const config = useMemo(() => MODE_CONFIG[mode] || MODE_CONFIG.game, [mode]);

  const value = useMemo(() => ({
    mode,
    setMode,
    density,
    setDensity,
    toggleCompact,
    isCompact: density === "compact",
    config,
    isWork: mode === "work",
    isGame: mode === "game",
    isMinimal: mode === "minimalist",
    isADHD: mode === "adhd",
    showGameFeatures: config.showGameFeatures,
    showADHDFeatures: config.showADHDFeatures,
    animationIntensity: config.animationIntensity,
  }), [mode, setMode, density, setDensity, toggleCompact, config]);

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

export function useMode() {
  const ctx = useContext(ModeContext);
  if (!ctx) throw new Error("useMode must be used within ModeProvider");
  return ctx;
}
